import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, Wrench, MessageSquare, CheckCircle2, XCircle } from "lucide-react";

export default function PlaygroundTracePanel({ trace }) {
    const { t } = useTranslation();
    const toolCalls = trace.filter((e) => e.type === "tool_call").length;

    return (
        <div className="space-y-2">
            <h3 className="text-sm font-semibold">
                {t("admin.support.toolTrace", "Tool Execution Trace")} ({toolCalls} {t("admin.support.calls", "calls")})
            </h3>
            <div className="space-y-1.5">
                {trace.map((entry, i) => <TraceEntry key={i} entry={entry} />)}
            </div>
        </div>
    );
}

function TraceEntry({ entry }) {
    const [open, setOpen] = useState(false);

    if (entry.type === "tool_call") {
        return (
            <div className="border rounded-md bg-blue-50 dark:bg-blue-950/30">
                <button onClick={() => setOpen(!open)} className="flex items-center gap-2 w-full px-3 py-2 text-left text-sm">
                    <Wrench className="w-4 h-4 text-blue-600 shrink-0" />
                    <span className="font-medium text-blue-800 dark:text-blue-300">{entry.toolName}</span>
                    <span className="text-xs text-muted-foreground ml-1">call</span>
                    {open ? <ChevronDown className="w-3.5 h-3.5 ml-auto" /> : <ChevronRight className="w-3.5 h-3.5 ml-auto" />}
                </button>
                {open && (
                    <pre className="mx-3 mb-3 text-xs bg-white dark:bg-background border rounded p-2 overflow-x-auto whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
                        {JSON.stringify(entry.args, null, 2)}
                    </pre>
                )}
            </div>
        );
    }

    if (entry.type === "tool_result") {
        const success = entry.rawResult?.success !== false;
        const Icon = success ? CheckCircle2 : XCircle;
        const color = success ? "text-green-600" : "text-red-600";
        const bg = success ? "bg-green-50 dark:bg-green-950/30" : "bg-red-50 dark:bg-red-950/30";
        return (
            <div className={`border rounded-md ${bg}`}>
                <button onClick={() => setOpen(!open)} className="flex items-center gap-2 w-full px-3 py-2 text-left text-sm">
                    <Icon className={`w-4 h-4 shrink-0 ${color}`} />
                    <span className="font-medium">{entry.toolName}</span>
                    <span className="text-xs text-muted-foreground ml-1">result</span>
                    <ResultSummary result={entry.rawResult} />
                    {open ? <ChevronDown className="w-3.5 h-3.5 ml-auto" /> : <ChevronRight className="w-3.5 h-3.5 ml-auto" />}
                </button>
                {open && (
                    <pre className="mx-3 mb-3 text-xs bg-white dark:bg-background border rounded p-2 overflow-x-auto whitespace-pre-wrap break-all max-h-80 overflow-y-auto">
                        {JSON.stringify(entry.rawResult, null, 2)}
                    </pre>
                )}
            </div>
        );
    }

    if (entry.type === "assistant") {
        return (
            <div className="border rounded-md bg-purple-50 dark:bg-purple-950/30 px-3 py-2">
                <div className="flex items-start gap-2">
                    <MessageSquare className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
                    <div className="text-sm prose prose-sm max-w-none break-all" dangerouslySetInnerHTML={{ __html: entry.content }} />
                </div>
            </div>
        );
    }
    return null;
}

function ResultSummary({ result }) {
    if (!result || typeof result === "string") return null;
    const parts = [];
    if (result.documents?.length) parts.push(`${result.documents.length} doc(s)`);
    if (result.videos?.length) parts.push(`${result.videos.length} video(s)`);
    if (result.workflows?.length) parts.push(`${result.workflows.length} workflow(s)`);
    if (result.document?.title) parts.push(result.document.title);
    if (result.error) parts.push(result.error);
    if (!parts.length) return null;
    return <span className="text-xs text-muted-foreground ml-1">— {parts.join(", ")}</span>;
}
